import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, message, Space, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FileExcelOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import DataTable, { type DataTableRef } from '../../components/Charts/DataTable';
import StatsKPIGroup, { type KPIItem } from '../../components/Business/StatsKPIGroup';
import { ForgeQueryBuilder, type FieldConfig, type ForgeQuery } from '../../components/ForgeQueryBuilder';
import {
  depositFinancialApi,
  type DepositFinancialItem,
  type DepositFinancialListRequest,
  type DepositFinancialSummary,
} from '../../services/statistics/deposit-financial';
import { useShopMap } from '../../services/store/organization.store';
import { OrderTypeMap } from '../../types/process/order';
import { useLocation } from 'react-router-dom';

const { Text, Title } = Typography;

const formatRawText = (value?: string | number): string => {
  const text = String(value ?? '').trim();
  return text || '-';
};

const formatDateTime = (value?: string): string => {
  return formatRawText(value);
};

const parseForgeQueryFromSearch = (search: string): ForgeQuery | undefined => {
  const searchParams = new URLSearchParams(search);
  const value = searchParams.get('q');
  if (!value) return undefined;
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {
    try {
      const parsed = JSON.parse(decodeURIComponent(value));
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const normalizeIncomingQuery = (query?: ForgeQuery): ForgeQuery | undefined => {
  if (!query?.filter || typeof query.filter !== 'object') {
    return query;
  }

  const filter = { ...(query.filter as Record<string, any>) };
  if (!filter.cashflowCommissionGeneratedAt) {
    if (filter.cashflowOrderCreatedAt) {
      filter.cashflowCommissionGeneratedAt = filter.cashflowOrderCreatedAt;
    } else if (filter.mainCreatedAt) {
      filter.cashflowCommissionGeneratedAt = filter.mainCreatedAt;
    }
  }

  return { ...query, filter };
};

const buildDefaultQuery = (): ForgeQuery => {
  const start = dayjs().startOf('month').format('YYYY-MM-DD');
  const end = dayjs().endOf('month').format('YYYY-MM-DD');
  return {
    filter: {
      cashflowCommissionGeneratedAt: {
        operator: 'between',
        value: [start, end],
      },
    },
  };
};

const RRZFinancialLedger: React.FC = () => {
  const location = useLocation();
  const { shopList, shopMap } = useShopMap();
  const tableRef = useRef<DataTableRef>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState<DepositFinancialItem[]>([]);
  const [summary, setSummary] = useState<DepositFinancialSummary>({
    totalCount: 0,
    totalAmount: 0,
    offlineDeductAmount: 0,
    platformSplitAmount: 0,
    returnSplitAmount: 0,
    alipayCommissionAmount: 0,
    commissionNetAmount: 0,
  });
  const [pageInfo, setPageInfo] = useState({ page: 1, pageSize: 20, total: 0 });
  const initialForcedQuery = useMemo(() => normalizeIncomingQuery(parseForgeQueryFromSearch(location.search)) || buildDefaultQuery(), [location.search]);
  const [lastQuery, setLastQuery] = useState<ForgeQuery>(initialForcedQuery);

  const queryFields: FieldConfig[] = useMemo(() => [
    { key: 'cashflowCommissionGeneratedAt', label: '佣金生成时间', type: 'date', operators: ['between'], format: 'YYYY-MM-DD', placeholder: ['开始日期', '结束日期'] },
    {
      key: 'storeId',
      label: '店铺',
      type: 'select',
      operators: ['eq'],
      options: shopList.map((shop) => ({ label: `${shop.name}`, value: Number(shop.code) })),
    },
    {
      key: 'order_type',
      label: '订单类型',
      type: 'select',
      operators: ['eq'],
      options: Object.entries(OrderTypeMap).map(([value, label]) => ({ label, value: Number(value) })),
    },
    { key: 'billId', label: '账单ID', type: 'string', operators: ['like'] },
    { key: 'type', label: '账户类型', type: 'string', operators: ['eq'] },
    { key: 'fromTo', label: '收支方向', type: 'string', operators: ['like'] },
    { key: 'money', label: '金额', type: 'string', operators: ['like'] },
    { key: 'body', label: '交易说明', type: 'string', operators: ['regex'] },
    { key: 'orderNo', label: '订单号', type: 'string', operators: ['like'] },
    { key: 'tradeNo', label: '交易单号', type: 'string', operators: ['like'] },
    { key: 'fromLogId', label: '来源日志ID', type: 'string', operators: ['eq'] },
    { key: 'fromLogType', label: '来源日志类型', type: 'string', operators: ['eq'] },
    { key: 'orderId', label: '订单ID', type: 'string', operators: ['like'] },
    { key: 'name', label: '用户姓名', type: 'string', operators: ['like'] },
    { key: 'merchName', label: '商户名称', type: 'string', operators: ['like'] },
    { key: 'period', label: '期数', type: 'string', operators: ['eq'] },
  ], [shopList]);

  const columns: ColumnsType<DepositFinancialItem> = [
    {
      title: '账单ID',
      key: 'billId',
      width: 170,
      fixed: 'left',
      render: (_: any, rec) => (
        <Text copyable={{ text: rec.main?.id || '' }} style={{ fontSize: 12 }}>
          {rec.main?.id || '-'}
        </Text>
      ),
    },
    {
      title: '账户类型',
      key: 'type',
      width: 120,
      render: (_: any, rec) => formatRawText(rec.main?.type),
    },
    {
      title: '收支方向',
      key: 'fromTo',
      width: 140,
      render: (_: any, rec) => formatRawText(rec.main?.from_to),
    },
    {
      title: '金额',
      key: 'money',
      width: 120,
      render: (_: any, rec) => formatRawText(rec.main?.money),
    },
    {
      title: '变动后余额',
      key: 'afterNum',
      width: 140,
      render: (_: any, rec) => formatRawText(rec.main?.after_num),
    },
    {
      title: '交易说明',
      key: 'body',
      width: 180,
      render: (_: any, rec) => formatRawText(rec.main?.body),
    },
    {
      title: '店铺',
      key: 'storeName',
      width: 150,
      render: (_: any, rec) => {
        const sid = Number(rec.serverId);
        if (Number.isFinite(sid)) {
          return shopMap.get?.(sid)?.name || formatRawText(rec.serverId);
        }
        return formatRawText(rec.serverId);
      },
    },
    {
      title: '订单号',
      key: 'orderNo',
      width: 170,
      render: (_: any, rec) => (
        <Text copyable={{ text: rec.main?.order_no || '' }} style={{ fontSize: 12 }}>
          {rec.main?.order_no || '-'}
        </Text>
      ),
    },
    {
      title: '交易单号',
      key: 'tradeNo',
      width: 120,
      render: (_: any, rec) => formatRawText(rec.main?.trade_no),
    },
    {
      title: '账单创建时间',
      key: 'mainCreatedAt',
      width: 160,
      render: (_: any, rec) => formatDateTime(rec.main?.created_at),
    },
    {
      title: '来源日志ID',
      key: 'fromLogId',
      width: 120,
      render: (_: any, rec) => formatRawText(rec.main?.from_log_id),
    },
    {
      title: '来源日志类型',
      key: 'fromLogType',
      width: 120,
      render: (_: any, rec) => formatRawText(rec.main?.from_log_type),
    },
    {
      title: '订单ID',
      key: 'orderId',
      width: 170,
      render: (_: any, rec) => (
        <Text copyable={{ text: rec.main?.order_id || '' }} style={{ fontSize: 12 }}>
          {rec.main?.order_id || '-'}
        </Text>
      ),
    },
    {
      title: '用户姓名',
      key: 'name',
      width: 120,
      render: (_: any, rec) => formatRawText(rec.main?.name),
    },
    {
      title: '商户名称',
      key: 'merchName',
      width: 180,
      render: (_: any, rec) => formatRawText(rec.main?.merch_name),
    },
    {
      title: '期数',
      key: 'period',
      width: 120,
      render: (_: any, rec) => formatRawText(rec.main?.period),
    },
  ];

  const fetchList = useCallback(async (query: ForgeQuery, page: number, pageSize: number) => {
    setLoading(true);
    try {
      const params = { ...query, page, pageSize } as DepositFinancialListRequest;
      const resp = await depositFinancialApi.list(params);
      const data = resp.data;
      setRows(data?.items || []);
      if (data?.pagination) {
        setPageInfo({
          page: data.pagination.page,
          pageSize: data.pagination.page_size,
          total: data.pagination.total,
        });
      } else {
        setPageInfo(prev => ({ ...prev, page, pageSize }));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSummary = useCallback(async (query: ForgeQuery) => {
    try {
      const params = { ...query } as DepositFinancialListRequest;
      const resp = await depositFinancialApi.summary(params);
      setSummary(resp.data || {
        totalCount: 0,
        totalAmount: 0,
        offlineDeductAmount: 0,
        platformSplitAmount: 0,
        returnSplitAmount: 0,
        alipayCommissionAmount: 0,
        commissionNetAmount: 0,
      });
    } catch (error) {
      console.error('获取人人租财务汇总失败:', error);
      message.error('获取汇总数据失败');
    }
  }, []);

  const handleSearch = async (query: ForgeQuery) => {
    setLastQuery(query);
    const nextPage = 1;
    setPageInfo(prev => ({ ...prev, page: nextPage }));
    await Promise.all([
      fetchList(query, nextPage, pageInfo.pageSize),
      fetchSummary(query),
    ]);
  };

  useEffect(() => {
    setLastQuery(initialForcedQuery);
    Promise.all([
      fetchList(initialForcedQuery, 1, pageInfo.pageSize),
      fetchSummary(initialForcedQuery),
    ]);
  }, [fetchList, fetchSummary, pageInfo.pageSize, initialForcedQuery]);

  const handlePageChange = (page: number, pageSize?: number) => {
    const nextSize = pageSize || pageInfo.pageSize;
    const nextPage = nextSize !== pageInfo.pageSize ? 1 : page;
    setPageInfo(prev => ({ ...prev, page: nextPage, pageSize: nextSize }));
    fetchList(lastQuery, nextPage, nextSize);
  };

  const handleExportAll = useCallback(async () => {
    setExporting(true);
    try {
      const exportPageSize = 1000;
      let page = 1;
      let totalPages = 1;
      let total = 0;
      const allRows: DepositFinancialItem[] = [];

      while (page <= totalPages) {
        const params = { ...lastQuery, page, pageSize: exportPageSize } as DepositFinancialListRequest;
        const resp = await depositFinancialApi.list(params);
        const data = resp.data;
        const items = data?.items || [];
        const pagination = data?.pagination;

        allRows.push(...items);
        total = pagination?.total ?? allRows.length;
        totalPages = Math.max(pagination?.total_pages ?? Math.ceil(total / exportPageSize), 1);

        if (items.length === 0 || allRows.length >= total) {
          break;
        }
        page += 1;
      }

      tableRef.current?.exportCSV({ filename: 'rrz-financial-ledger.csv', rows: allRows });
      message.success(`导出成功，共 ${allRows.length} 条记录`);
    } catch (error) {
      console.error('导出人人租财务明细失败:', error);
      message.error('导出失败，请稍后重试');
    } finally {
      setExporting(false);
    }
  }, [lastQuery]);

  const kpiItems = useMemo<KPIItem[]>(() => [
    {
      title: '佣金净支出',
      value: -Math.abs(Number(summary.commissionNetAmount || 0)),
      prefix: '¥',
      precision: 2,
      color: '#f5222d',
    },
    {
      title: '线下支付倒扣商家',
      value: -Math.abs(Number(summary.offlineDeductAmount || 0)),
      prefix: '¥',
      precision: 2,
      color: '#fa8c16',
    },
    {
      title: '平台分账',
      value: -Math.abs(Number(summary.platformSplitAmount || 0)),
      prefix: '¥',
      precision: 2,
      color: '#1890ff',
    },
    {
      title: '退回分账',
      value: Math.abs(Number(summary.returnSplitAmount || 0)),
      prefix: '¥',
      precision: 2,
      color: '#52c41a',
    },
    {
      title: '支付宝佣金',
      value: -Math.abs(Number(summary.alipayCommissionAmount || 0)),
      prefix: '¥',
      precision: 2,
      color: '#722ed1',
    },
    {
      title: '筛选记录数',
      value: Number(summary.totalCount || 0),
      precision: 0,
      color: '#13c2c2',
    },
  ], [summary]);

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>人人租财务明细</Title>
      <div style={{ marginBottom: 12 }}>
        <StatsKPIGroup items={kpiItems} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <ForgeQueryBuilder fields={queryFields} defaultQuery={initialForcedQuery} onSearch={handleSearch} />
      </div>
      <Card
        title="人人租财务明细列表（MongoDB deposits）"
        extra={(
          <Space>
            <Button icon={<FileExcelOutlined />} loading={exporting} onClick={handleExportAll}>导出Excel</Button>
          </Space>
        )}
      >
        <DataTable
          ref={tableRef as any}
          loading={loading}
          columns={columns}
          dataSource={rows as any}
          rowKey={(r: DepositFinancialItem) => String(r.id || r.main?.id || r.main?.order_no)}
          pagination={{
            current: pageInfo.page,
            pageSize: pageInfo.pageSize,
            total: pageInfo.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: handlePageChange,
          }}
        />
      </Card>
    </div>
  );
};

export default RRZFinancialLedger;
